import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getAWAUserQueryOptions } from '@/queries/awa';
import { AWAGlobalApiService } from '@/services/global-api/awa-global-api.service';

export function stripAWAPath(path: string, stripOrg = false) {
  // First remove /awa prefix if it exists
  let strippedPath = path.replace(/^\/awa\/?/, '');

  if (stripOrg) {
    // Remove the workspace/org id segment (and its trailing slash) — both the
    // legacy UUID form and the modern FlakeID form (`ws_4xad4cHljsU`). Without
    // the FlakeID branch the org id survives, and callers that rebuild
    // `/${workspaceId}/${redirectTo}` produce a double-prefixed path that
    // matches no route. Both patterns are start-anchored to the leading
    // segment: entity ids deeper in the path (a UUID qr id in
    // `/:workspaceId/qr/:qrId/assign`, a `ws_`-prefixed id) must survive, or
    // the rebuilt redirect drops them and lands on a route that doesn't exist.
    const uuidOrgPattern = /^\/?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\//;
    const flakeIdOrgPattern = /^\/?ws_[A-Za-z0-9]+\//;
    strippedPath = strippedPath.replace(uuidOrgPattern, '').replace(flakeIdOrgPattern, '');
  }

  // Remove any leading or trailing slashes
  return strippedPath.replace(/^\/+|\/+$/g, '');
}

// Paths a user is on to submit an inspection — directly, via prestart, or from a
// QR code (which lands on the asset / qr-assign pages). Anyone on one of these is
// never prompted or redirected by the AWA→member migration guard.
export const INSPECTION_FLOW_SEGMENTS = ['/inspection', '/prestart', '/qr/', '/assets/'] as const;

// The QR / pre-start intent must survive the auth + onboarding chain. Its
// intermediate hops (`/auth/onboarding`, the workspace-redirect) carry the
// original path only in the `redirectTo` query param, not in the pathname, so
// checking `location.pathname` alone drops the exemption there and the migration
// guard wrongly bounces an inspection submitter to GBMW. `redirectTo` is stored
// without a leading slash (see `stripAWAPath`), so normalise before matching.
export function pathIndicatesInspectionFlow(path: string | null | undefined) {
  if (!path) return false;
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  return INSPECTION_FLOW_SEGMENTS.some((segment) => normalisedPath.includes(segment));
}

export function useIsAWA() {
  const isAWA = useMemo(() => {
    const formsUrl = new URL(import.meta.env.VITE_FORMS_URL);
    const hostnameMatch = window.location.hostname === formsUrl.hostname;

    if (import.meta.env.DEV) {
      return hostnameMatch && window.location.port === formsUrl.port;
    }

    return hostnameMatch;
  }, []);

  return { isAWA };
}

export function checkIfUserProfileComplete(user: Awaited<ReturnType<typeof AWAGlobalApiService.getUser>>) {
  return !!user?.id && !!user?.firstName && !!user?.lastName && !!user?.companyName && !!user?.avatarUrl && !!user?.signatureUrl;
}

export function useUser() {
  return useQuery({
    ...getAWAUserQueryOptions(),
    retry: false,
  });
}
